import type { StationId } from '@/domain/types';
import type { SnapshotRepository, SnapshotRow as PortSnapshotRow } from '@/persistence/ports';
import type { Evse3Database, SnapshotRow } from '../db';

/**
 * The port view is narrower than the Dexie row (no kind/direction/summary).
 * The repo defaults the missing fields so the domain consumer can stay
 * focused on what it cares about.
 */
export class SnapshotRepositoryDexie implements SnapshotRepository {
  constructor(private readonly db: Evse3Database) {}

  async byStation(stationId: StationId): Promise<readonly PortSnapshotRow[]> {
    const rows = await this.db.snapshots.where('stationId').equals(stationId).toArray();
    return rows.map(rowToPort);
  }

  async upsert(row: PortSnapshotRow): Promise<void> {
    const full: SnapshotRow = {
      id: row.id,
      ...(row.stationId !== undefined ? { stationId: row.stationId } : {}),
      kind: 'export',
      direction: 'out',
      createdAt: row.createdAt,
      schemaVersion: 1,
      payload: row.payload,
      summary: { configKeyCount: 0, profileCount: 0, offlineQueueCount: 0 },
    };
    await this.db.snapshots.put(full);
  }

  async delete(id: string): Promise<void> {
    await this.db.snapshots.delete(id);
  }
}

function rowToPort(row: SnapshotRow): PortSnapshotRow {
  return {
    id: row.id,
    ...(row.stationId !== undefined ? { stationId: row.stationId } : {}),
    createdAt: row.createdAt,
    kind: row.kind,
    payload: row.payload,
  };
}
